<script setup lang="ts">
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import { watch, onMounted, onUnmounted } from "vue";
import { useRouter } from 'vue-router'
import { useAuthStore } from './stores/authStore'
import { useSocketStore } from './stores/socketStore'
import { useToast } from 'primevue/usetoast'
import NetworkStatusIndicator from './components/common/NetworkStatusIndicator.vue';
import ErrorBoundary from './components/ErrorBoundary.vue';
import { PERMISSIONS } from './constants/permissions';
import * as Sentry from '@sentry/vue';
import { app } from './main';

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// Define isDev as a reactive value
const isDev = import.meta.env.DEV

// Check if user has dispatch permissions
const isDispatcher = () => {
  return authStore.hasPermission(PERMISSIONS.DISPATCH_VIEW);
}


// Socket event listeners for request notifications
onMounted(() => {
  // Initialize socket store safely within component context
  try {
    const socketStore = useSocketStore()
    
    // Add socket error handlers
    socketStore.socket.on('connect_error', (error) => {
      Sentry.captureException(error, {
        tags: { component: 'App.vue', type: 'socket_connection' },
        extra: { errorType: 'connect_error', message: error.message }
      });
      console.error('Socket connection error:', error);
    });

    socketStore.socket.on('error', (error) => {
      Sentry.captureException(error, {
        tags: { component: 'App.vue', type: 'socket_error' },
        extra: { errorType: 'general_error', message: error.message }
      });
      console.error('Socket error:', error);
    });
    
    // Listen for request completed events
    socketStore.socket.on('requestCompleted', (data) => {
      try {
        if (isDispatcher()) {
          toast.add({
            severity: 'info',
            summary: 'Request Completed',
            detail: data.message,
            life: 5000
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'App.vue', event: 'requestCompleted' },
          extra: { data }
        });
      }
    });

    // Listen for request result submitted events
    socketStore.socket.on('requestResultSubmitted', (data) => {
      try {
        if (isDispatcher()) {
          toast.add({
            severity: data.successful ? 'success' : 'warn',
            summary: data.successful ? 'Request Successful' : 'Request Unsuccessful',
            detail: data.message,
            life: 5000
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'App.vue', event: 'requestResultSubmitted' },
          extra: { data }
        });
      }
    });
    
    // Clean up socket listeners on component unmount
    onUnmounted(() => {
      socketStore.socket.off('requestCompleted');
      socketStore.socket.off('requestResultSubmitted');
      socketStore.socket.off('connect_error');
      socketStore.socket.off('error');
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'App.vue', phase: 'socket_init' },
      level: 'error'
    });
    console.error('Error initializing socket in App.vue:', error);
  }
});

watch(() => authStore.isAuthenticated, (newValue, oldValue) => {
  if (!newValue && oldValue) {
    // User has been logged out
    void router.push('/login')
  }
})

// Debug Sentry initialization (intentional debug logging)
// eslint-disable-next-line no-console
console.log('[SENTRY DEBUG] Initializing Sentry...');
// eslint-disable-next-line no-console
console.log('[SENTRY DEBUG] DSN:', import.meta.env.VITE_SENTRY_DSN ? 'Set' : 'Not set');
// eslint-disable-next-line no-console
console.log('[SENTRY DEBUG] Environment:', import.meta.env.MODE);
// eslint-disable-next-line no-console
console.log('[SENTRY DEBUG] Domain:', window.location.hostname);

// Initialize Sentry with error handling
try {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://e0d9630c99057861593bd7c8d203a28c@o4507548914614272.ingest.us.sentry.io/4507548918251520",
    logErrors: true,
    environment: import.meta.env.MODE,
    // Release tracking - correlates errors with deployments
    // __APP_VERSION__ is injected at build time from package.json via vite.config.js
    release: `rms-frontend@${__APP_VERSION__}`,
    // Allow URLs from both frontend domains
    allowUrls: [
      'https://rms.janssenequipment.com/',
      'http://localhost:5173/' // Include localhost for development
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: ['https://rms.janssenequipment.com/api/v1/']
      }),
      Sentry.feedbackIntegration({
        // Let Sentry handle injection, but we'll also create our own widget
        autoInject: true,
        colorScheme: 'system',
        showEmail: false,
        triggerLabel: 'Report Bug',
        position: 'bottom-left',
        isNameRequired: true, // Make name field required
        enableScreenshot: true, // Enable screenshot button (default is true)
        // Customize text to mention screenshot capability
        messagePlaceholder: 'Describe the issue you encountered.',
        submitButtonLabel: 'Send Feedback',
        successMessageText: 'Thank you for your feedback!'
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.15 : 0.05, // Capture 15% in production, 5% in development
    // Error Event Sampling - reduce quota usage in development
    sampleRate: import.meta.env.MODE === 'production' ? 1.0 : 0.3, // Capture 100% errors in production, 30% in development
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/(new)?rms\.janssenequipment\.com\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Enhanced error filtering to prevent quota errors
    beforeSend(event, hint) {
      // Don't send Sentry quota errors to Sentry (prevents recursive quota issues)
      if (hint.originalException?.message?.includes('403') && 
          hint.originalException?.config?.url?.includes('sentry.io')) {
        console.warn('[SENTRY] Blocked quota error from being sent to prevent recursion');
        return null;
      }
      
      // Filter out other Sentry-related HTTP errors
      if (hint.originalException?.config?.url?.includes('sentry.io')) {
        const status = hint.originalException?.response?.status;
        if ([403, 429, 413].includes(status)) {
          console.warn(`[SENTRY] Blocked ${status} error from Sentry API to prevent recursion`);
          return null;
        }
      }
      
      return event;
    },
    // Add transport error handling
    transport: (options) => {
      const transport = Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport);
      return transport({
        ...options,
        // Retry configuration for quota/rate limit errors
        requestOptions: {
          ...options.requestOptions,
          fetchOptions: {
            ...options.requestOptions?.fetchOptions,
            // Add timeout to prevent hanging requests
            signal: AbortSignal.timeout(10000) // 10 second timeout
          }
        }
      });
    }
  });
  // eslint-disable-next-line no-console
  console.log('[SENTRY DEBUG] Sentry initialized successfully');
} catch (error) {
  console.error('[SENTRY ERROR] Failed to initialize Sentry:', error);
  // Don't let Sentry initialization failure break the app
  if (import.meta.env.DEV) {
    console.warn('[SENTRY] Running in development mode without Sentry due to initialization error');
  }
}
</script>

<template>
  <ErrorBoundary 
    fallback="Something went wrong. Please refresh the page."
    :show-details="isDev"
  >
    <Toast />
    <ConfirmDialog />
    <ConfirmDialog group="confirm-dialog" />
    <ConfirmDialog group="defer-info" />
    <NetworkStatusIndicator />
    <RouterView />
  </ErrorBoundary>
</template>

<style>
#sentry-feedback {
  position: fixed !important;
  bottom: 0 !important;
  right: 0 !important;
  z-index: 1000 !important;
}
</style>
